import React, { useEffect, useState } from 'react';
import { RouteNames } from '../../../shared/enums/RouteNames';
import sharedHistory from '../../../shared/History';
import { Auth } from 'aws-amplify';
import {
  ValidateEmail,
  ValidatePassword,
} from '../../../shared/AccountPolicy/AccountPolicy';
import { AuthCodes } from '../../../shared/enums/AuthCodes';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import {
  SetUserAttributes,
  SetUserEmail,
} from '../../../store/actions/user/UserActions';
import Toast from '../../../shared/Notifications/Toast';
import _ from 'lodash';
import './AccountForm.scss';
import ManageHabitatsApiHelpers from '../../views/manageHabitats/ManageHabitatsApiHelpers';
import { PasswordInput } from '../PasswordInput/PasswordInput';
import { ILocationServiceObj } from '../../../shared/apiServices/IApiInterfaces';

export const FormIds = {
  username: 'Username',
  firstName: 'FirstName',
  lastName: 'LastName',
  email: 'Email',
  password: 'Password',
  site: 'Site',
  createBtn: 'CreateAccount',
  saveBtn: 'SaveAccount',
  cancelBtn: 'Cancel',
  deactivateBtn: 'DeactivateAccount',
};

const VALID = 'valid';
const INVALID = 'invalid';

const AccountForm = ({
  setUserEmail,
  setUserAttributes,
  userObject,
  type,
}: IAccountFormProps) => {
  const [firstName, setFirstName] = useState(
    userObject?.attributes?.given_name ?? ''
  );
  const [lastName, setLastName] = useState(
    userObject?.attributes?.family_name ?? ''
  );
  const [username, setUsername] = useState(
    userObject?.attributes?.preferred_username ?? ''
  );
  const [password, setPassword] = useState(userObject ? 'PASSWORD' : '');
  const [email, setEmail] = useState(userObject?.attributes?.email ?? '');
  const [site, setSite] = useState(userObject?.attributes?.locale ?? '');

  const [emailValidation, setEmailValidation] = useState(
    ValidateEmail(userObject?.attributes?.email ?? '')
  );
  const [passwordValidation, setPasswordValidation] = useState(
    {} as IFieldValidation
  );
  const [formValid, setFormValid] = useState(false);
  const [locations, setLocations] = useState<ILocationServiceObj[]>([]);

  useEffect(() => {
    let allValid = true;
    allValid = allValid && firstName.length > 0;
    allValid = allValid && lastName.length > 0;
    allValid = allValid && username.length > 0;
    allValid = allValid && site.length > 0;
    allValid = allValid && ValidateEmail(email).class === VALID;
    allValid =
      type === 'create'
        ? allValid && ValidatePassword(password).class === VALID
        : allValid && !_.isEqual(getAttributes(), userObject?.attributes);
    setFormValid(allValid);
  }, [firstName, lastName, username, site, email, password]);

  useEffect(() => {
    const fetchLocationData = async () => {
      const newLocations = await getLocations();
      setLocations(newLocations);
    };
    fetchLocationData();
  }, []);

  const getAttributes = () => ({
    ...userObject?.attributes,
    ...(type === 'update' && { email: email.trim() }),
    ...(type === 'create' && { 'custom:tos-accepted': 'false' }),
    given_name: firstName.trim(),
    family_name: lastName.trim(),
    preferred_username: username.trim(),
    locale: site,
  });

  async function createAccount() {
    try {
      if (type === 'create') {
        await Auth.signUp({
          username: email.trim(),
          password: password,
          attributes: getAttributes(),
        });
        setUserEmail(email);
        sharedHistory.push(RouteNames.CreateAccountSuccess);
      } else {
        if (userObject) {
          const user = await Auth.currentAuthenticatedUser();
          await Auth.updateUserAttributes(user, getAttributes());
          setUserEmail(email);
          const newUser = await Auth.currentAuthenticatedUser();
          setUserAttributes(newUser.attributes);
          Toast.show('User updated successfully');
        }
      }
    } catch (authError: any) {
      if (
        [AuthCodes.UsernameExists, AuthCodes.AliasExists].includes(
          authError.code
        )
      ) {
        setEmailValidation({ class: INVALID, message: authError.message });
      } else {
        throw authError;
      }
    }
  }

  async function deactivateAccount() {
    if (userObject) {
      sharedHistory.push(RouteNames.DeactivateAccount);
    }
  }

  const getLocations = async (): Promise<ILocationServiceObj[]> => {
    try {
      const initialLocations: ILocationServiceObj[] =
        await ManageHabitatsApiHelpers.getAllHabitatLocations();
      return initialLocations;
    } catch (ex) {
      Toast.show('Error Getting Sites');
      console.error();
      return [];
    }
  };

  return (
    <>
      <div className="row account-form-top-row">
        <div className="account-form-subtitle col s9">
          {type === 'update' && <h5>My Information</h5>}
        </div>
        <button
          aria-label={FormIds.cancelBtn}
          className="btn btn-tertiary"
          onClick={sharedHistory.goBack}
        >
          Cancel
        </button>
        <button
          aria-label={type === 'create' ? FormIds.createBtn : FormIds.saveBtn}
          className="btn btn-primary"
          onClick={createAccount}
          disabled={!formValid}
        >
          {type === 'create' ? 'Create Account' : 'Save'}
        </button>
      </div>
      <div className="row">
        <div className="col s9 account-form-content">
          <div className="row">
            <div className="input-field col s6">
              <input
                id={FormIds.firstName}
                aria-label={FormIds.firstName}
                type="text"
                className={firstName?.length > 0 ? VALID : ''}
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFirstName(e.target?.value || '')
                }
              />
              <label
                className={`${firstName ? 'active' : ''}`}
                htmlFor={FormIds.firstName}
              >
                First Name
              </label>
              <span className="helper-text"></span>
            </div>
            <div className="input-field col s6">
              <input
                id={FormIds.lastName}
                aria-label={FormIds.lastName}
                type="text"
                className={lastName?.length > 0 ? VALID : ''}
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setLastName(e.target?.value || '')
                }
              />
              <label
                className={`${lastName ? 'active' : ''}`}
                htmlFor={FormIds.lastName}
              >
                Last Name
              </label>
              <span className="helper-text"></span>
            </div>
          </div>
          <div className="row">
            <div className="input-field col s6">
              <input
                id={FormIds.email}
                aria-label={FormIds.email}
                type="text"
                className={emailValidation.class}
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target?.value || '');
                  setEmailValidation(ValidateEmail(e.target?.value || ''));
                }}
              />
              <label
                className={`${email ? 'active' : ''}`}
                htmlFor={FormIds.email}
              >
                Email Address
              </label>
              <span
                className="helper-text"
                data-error={emailValidation.message}
              ></span>
            </div>
            {type === 'create' ? (
              <div className="input-field col s6">
                <PasswordInput
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target?.value || '');
                    setPasswordValidation(
                      ValidatePassword(e.target?.value || '')
                    );
                  }}
                  value={password}
                  id={FormIds.password}
                  testId={FormIds.password}
                  aria-label={FormIds.password}
                  inputClassName={passwordValidation.class}
                  labelClassName={`${password ? 'active' : ''}`}
                  helperText={passwordValidation.message}
                />
              </div>
            ) : (
              <div className="input-field col s6">
                <input
                  id={FormIds.username}
                  aria-label={FormIds.username}
                  type="text"
                  className={username?.length > 0 ? VALID : ''}
                  value={username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUsername(e.target?.value || '')
                  }
                />
                <label
                  className={`${username ? 'active' : ''}`}
                  htmlFor={FormIds.username}
                >
                  Username
                </label>
                <span className="helper-text"></span>
              </div>
            )}
          </div>
          <div className="row">
            {type === 'create' && (
              <div className="input-field col s6">
                <input
                  id={FormIds.username}
                  aria-label={FormIds.username}
                  type="text"
                  className={username?.length > 0 ? VALID : ''}
                  value={username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUsername(e.target?.value || '')
                  }
                />
                <label
                  className={`${username ? 'active' : ''}`}
                  htmlFor={FormIds.username}
                >
                  Username
                </label>
                <span className="helper-text"></span>
              </div>
            )}
            <div className="input-field col s6">
              <select
                data-testid={FormIds.site}
                id={FormIds.site}
                aria-label={FormIds.site}
                className="browser-default"
                value={site}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setSite(e.target?.value || '')
                }
              >
                <option value="" disabled>
                  Choose your site
                </option>
                {locations.map((loc) => (
                  <option key={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
          {type === 'update' &&
            <div className="row">
              <div className="input-field col s6">
                <button
                  data-testid={FormIds.deactivateBtn}
                  aria-label={FormIds.deactivateBtn}
                  className="btn btn-primary"
                  onClick={deactivateAccount}
                >
                  Deactivate Account
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </>
  );
};

const dispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>) => ({
  setUserEmail: (userEmail: string) => dispatch(SetUserEmail(userEmail)),
  setUserAttributes: (userAttributes: {}) =>
    dispatch(SetUserAttributes(userAttributes)),
});

export default connect(undefined, dispatchToProps)(AccountForm);
